/**
 * Interview Prep form logic (P5), kept pure so it can be tested without a DOM.
 */
import type {
  InterviewPrepCatalog,
  InterviewPrepRequest,
  InterviewType,
  InterviewTypeEntry,
  PrepContext,
  PrepFieldOrigin,
  PrepLimits,
  PrepProgressSummary,
} from '../store/api/interviewPrepApi';

export interface InterviewPrepFields {
  interviewType: InterviewType | null;
  targetRole: string;
  jobDescription: string;
}

export interface InterviewPrepLimits {
  targetRoleMaxChars: number;
  jobDescriptionMaxChars: number;
  drillTopicMaxChars?: number;
}

export const DEFAULT_PREP_LIMITS: PrepLimits = {
  roleTitleMaxChars: 120,
  seniorityMaxChars: 60,
  itemMaxChars: 200,
  listMaxItems: 12,
  sourceRefMaxChars: 300,
};

export type PracticeKind = 'interview' | 'question_drill';

/**
 * The request to send. Blank optional text is omitted (never sent as ""), and
 * nothing else is added: the client names a format and its own context, never
 * a scenario, persona or rubric.
 */
export function buildInterviewPrepRequest(fields: {
  interviewType: InterviewType;
  targetRole: string;
  jobDescription: string;
  prep?: PrepContext | null;
  practiceKind?: PracticeKind;
  drillTopic?: string;
  prepProfileId?: string | null;
}): InterviewPrepRequest {
  const targetRole = fields.targetRole.trim();
  const jobDescription = fields.jobDescription.trim();
  const drillTopic = fields.practiceKind === 'question_drill' ? (fields.drillTopic ?? '').trim() : '';
  return {
    interviewType: fields.interviewType,
    ...(targetRole ? { targetRole } : {}),
    ...(jobDescription ? { jobDescription } : {}),
    ...(fields.prep && !isEmptyPrep(fields.prep) ? { prep: fields.prep } : {}),
    ...(drillTopic ? { drillTopic } : {}),
    ...(fields.prepProfileId ? { prepProfileId: fields.prepProfileId } : {}),
  };
}

/**
 * Why the form cannot start yet, or null when it can. The server re-validates
 * everything; this only keeps the learner from sending what it would refuse.
 */
export function interviewPrepBlocker(
  fields: InterviewPrepFields & { practiceKind?: PracticeKind; drillTopic?: string; prep?: PrepContext | null },
  entry: InterviewTypeEntry | undefined,
  limits: InterviewPrepLimits,
  prepLimits: PrepLimits = DEFAULT_PREP_LIMITS
): string | null {
  if (!fields.interviewType || !entry) return 'Choose an interview type.';
  if (entry.status !== 'available') return 'This interview type has not been authored yet.';
  if (fields.targetRole.trim().length > limits.targetRoleMaxChars) {
    return `The target role must be at most ${limits.targetRoleMaxChars} characters.`;
  }
  if (fields.jobDescription.trim().length > limits.jobDescriptionMaxChars) {
    return `The job description must be at most ${limits.jobDescriptionMaxChars} characters.`;
  }
  if (fields.practiceKind === 'question_drill') {
    const topic = (fields.drillTopic ?? '').trim();
    if (!topic) return 'Choose a topic for the single-question drill.';
    const max = limits.drillTopicMaxChars ?? 200;
    if (topic.length > max) return `The drill topic must be at most ${max} characters.`;
  }
  if (fields.prep) {
    const problem = prepContextProblem(fields.prep, prepLimits);
    if (problem) return problem;
  }
  return null;
}

// ── the normalised preparation context, edited by the learner ─────────────────

export const PREP_LIST_FIELDS = ['responsibilities', 'requirements', 'skills', 'interviewTopics'] as const;
export type PrepListField = (typeof PREP_LIST_FIELDS)[number];

export const PREP_FIELD_LABELS: Record<PrepListField | 'roleTitle' | 'seniority', string> = {
  roleTitle: 'Role title',
  seniority: 'Seniority (only if the posting states it)',
  responsibilities: 'Responsibilities',
  requirements: 'Requirements',
  skills: 'Skills',
  interviewTopics: 'Likely interview topics',
};

export const ORIGIN_COPY: Record<PrepFieldOrigin, string> = {
  MODEL_INFERRED: 'Read from the posting by AI — check it',
  LEARNER_EDITED: 'Edited by you',
};

export function isEmptyPrep(c: PrepContext): boolean {
  return !c.roleTitle && !c.seniority && PREP_LIST_FIELDS.every((f) => c[f].items.length === 0);
}

/** One item per non-empty line of a textarea, trimmed. */
export function itemsFromText(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 0);
}

/** Replace a list's items; the field becomes LEARNER_EDITED only when the items actually changed. */
export function editPrepList(c: PrepContext, field: PrepListField, items: string[]): PrepContext {
  const same = items.length === c[field].items.length && items.every((v, i) => v === c[field].items[i]);
  if (same) return c;
  return { ...c, [field]: { items, origin: 'LEARNER_EDITED' as const } };
}

/** Replace a text field; empty clears it. LEARNER_EDITED only when the value changed. */
export function editPrepText(c: PrepContext, field: 'roleTitle' | 'seniority', value: string): PrepContext {
  const v = value.replace(/\s+/g, ' ').trim();
  if ((c[field]?.value ?? '') === v) return c;
  return { ...c, [field]: v ? { value: v, origin: 'LEARNER_EDITED' as const } : null };
}

/** What the server would refuse in an edited context, or null. */
export function prepContextProblem(c: PrepContext, limits: PrepLimits = DEFAULT_PREP_LIMITS): string | null {
  if ((c.roleTitle?.value.length ?? 0) > limits.roleTitleMaxChars) return `The role title must be at most ${limits.roleTitleMaxChars} characters.`;
  if ((c.seniority?.value.length ?? 0) > limits.seniorityMaxChars) return `The seniority must be at most ${limits.seniorityMaxChars} characters.`;
  for (const f of PREP_LIST_FIELDS) {
    if (c[f].items.length > limits.listMaxItems) return `${PREP_FIELD_LABELS[f]}: at most ${limits.listMaxItems} items.`;
    if (c[f].items.some((i) => i.length > limits.itemMaxChars)) return `${PREP_FIELD_LABELS[f]}: each item must be at most ${limits.itemMaxChars} characters.`;
  }
  return null;
}

/** "Read from jobs.example.com/roles/42 on 1 Oct 2026" — never more than the server kept. */
export function describeProvenance(c: PrepContext, locale = 'en-GB'): string {
  const when = new Date(c.provenance.fetchedAt);
  const date = Number.isNaN(when.getTime()) ? '' : ` on ${when.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  switch (c.provenance.source) {
    case 'paste':
      return `From the text you pasted${date}`;
    case 'upload':
      return `From your file ${c.provenance.sourceRef ?? ''}${date}`.replace(/\s+on/, ' on');
    case 'url':
      return `Read from ${c.provenance.sourceRef ?? 'a link'}${date}`;
    case 'marketplace':
      return `From a Marketplace opportunity${date}`;
  }
}

/** Topics to offer for a single-question drill: the context's likely topics, else its skills. */
export function drillTopicOptions(c: PrepContext | null): string[] {
  if (!c) return [];
  return c.interviewTopics.items.length ? c.interviewTopics.items : c.skills.items;
}

/** A short, honest progress line. Unmeasured attempts are counted, never scored. */
export function progressLine(s: PrepProgressSummary): string {
  if (s.attempts === 0) return 'No practice yet with this profile.';
  const parts = [`${s.attempts} attempt${s.attempts === 1 ? '' : 's'}`];
  if (s.measured === 0) parts.push('none measured yet');
  else parts.push(`average ${s.averageOverall} across ${s.measured} measured`);
  if (s.change !== null) parts.push(`${s.change >= 0 ? '+' : ''}${s.change} since your first measured attempt`);
  return parts.join(' · ');
}

/** Upload pre-check (the server re-checks): extension and size. */
export function uploadProblem(file: { name: string; size: number }, extensions: string[], maxBytes: number): string | null {
  const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : '';
  if (!extensions.includes(ext)) return `Upload a ${extensions.join(', ')} file, or paste the text instead.`;
  if (file.size > maxBytes) return `That file is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.`;
  return null;
}

export type SourceTab = 'paste' | 'upload' | 'url';

/** The source tabs this environment offers: paste always; upload and link only when the server says so. */
export function availableSourceTabs(catalog: InterviewPrepCatalog): SourceTab[] {
  const s = catalog.sources ?? { paste: true, upload: false, url: false, marketplace: false };
  return (['paste', 'upload', 'url'] as const).filter((t) => t === 'paste' || s[t]);
}
